import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import {
  Search,
  Save,
  ArrowLeft,
  Mail,
  Eye,
  EyeOff,
  Link2,
  MessageSquare,
  ImageIcon,
  Upload,
  Flag,
  Plus,
  ArrowUp,
  Settings,
  X
} from 'lucide-react';

const AppRegisterPageNew = () => {
  return (
    <main className="p-6 lg:p-10 space-y-8">
      {/* Page Title */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-2">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            className="h-12 w-12 rounded-xl">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
              App Settings
            </h1>
            <p className="text-base text-muted-foreground mt-1">
              Configure account and messaging settings
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative hidden lg:block">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search"
              className="pl-12 h-12 w-72 rounded-xl text-base"
            />
          </div>
          <Button
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground h-14 px-8 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3">
            <Save className="h-5 w-5" />
            Save Changes
          </Button>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Account Information */}
        <Card className="shadow-md rounded-2xl border-0">
          <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Settings className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl lg:text-2xl font-bold">
                  Account Information
                </CardTitle>
                <CardDescription className="text-base mt-1">
                  Client credentials and review links
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 px-6 lg:px-8 pb-8">
            {/* Client's Email */}
            <div className="space-y-2">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                Client's Email
              </Label>
              <Input className="h-12 text-base rounded-xl border-2 focus:border-primary px-4" />
            </div>

            {/* Password / Repeat Password */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-base font-semibold">Password</Label>
                <div className="relative">
                  <Input className="h-12 text-base rounded-xl border-2 focus:border-primary px-4 pr-12" />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"></button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">
                  Repeat Password
                </Label>
                <div className="relative">
                  <Input className="h-12 text-base rounded-xl border-2 focus:border-primary px-4 pr-12" />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"></button>
                </div>
              </div>
            </div>

            {/* Google Link */}
            <div className="space-y-2">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Link2 className="h-4 w-4 text-muted-foreground" />
                Google Link
              </Label>
              <Input className="h-12 text-base rounded-xl border-2 focus:border-primary px-4" />
            </div>

            {/* Yelp Link */}
            <div className="space-y-2">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Link2 className="h-4 w-4 text-muted-foreground" />
                Yelp Link
              </Label>
              <Input className="h-12 text-base rounded-xl border-2 focus:border-primary px-4" />
            </div>

            {/* Short Links */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-base font-semibold">
                  Short Link GMB
                </Label>
                <Input
                  readOnly
                  className="h-12 text-base rounded-xl border-2 px-4 bg-muted/50 text-muted-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">
                  Short Link Yelp
                </Label>
                <Input
                  readOnly
                  className="h-12 text-base rounded-xl border-2 px-4 bg-muted/50 text-muted-foreground"
                />
              </div>
            </div>

            {/* Feature Flags */}
            <div className="space-y-3 pt-2">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Flag className="h-4 w-4 text-muted-foreground" />
                Feature Flags
              </Label>
              <div className="flex items-center justify-between py-2">
                <span className="text-base">Notifications</span>
                <Switch className="data-[state=checked]:bg-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Message Configuration */}
        <Card className="shadow-md rounded-2xl border-0">
          <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl lg:text-2xl font-bold">
                  Message Configuration
                </CardTitle>
                <CardDescription className="text-base mt-1">
                  Customize review request messages
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-6 lg:px-8 pb-8">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* iPhone Preview */}
              <div className="flex justify-center lg:justify-start shrink-0">
                <div className="relative w-[260px]">
                  <div className="bg-foreground rounded-[3rem] p-[10px] shadow-2xl">
                    <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[100px] h-[24px] bg-foreground rounded-b-2xl z-10 flex items-center justify-center">
                      <div className="w-[50px] h-[4px] bg-muted-foreground/30 rounded-full" />
                    </div>
                    <div className="bg-background rounded-[2.4rem] overflow-hidden">
                      <div className="h-10" />
                      <div className="bg-primary/5 border-b border-border px-4 py-2.5">
                        <p className="text-xs font-semibold text-foreground text-center">
                          Messages
                        </p>
                      </div>
                      <div className="px-3 py-4 min-h-[380px] bg-background">
                        <div className="flex justify-start mb-2">
                          <div className="bg-primary/10 rounded-2xl rounded-bl-md px-3 py-2.5 max-w-[90%]">
                            <p className="text-[11px] text-foreground leading-relaxed"></p>
                          </div>
                        </div>
                      </div>
                      <div className="border-t border-border px-2.5 py-2 flex items-center gap-1.5 bg-muted/30">
                        <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center">
                          <Plus className="h-3 w-3 text-muted-foreground" />
                        </div>
                        <div className="flex-1 bg-background border border-border rounded-full px-3 py-1.5">
                          <span className="text-[10px] text-muted-foreground">
                            iMessage
                          </span>
                        </div>
                        <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center">
                          <ArrowUp className="h-3 w-3 text-primary-foreground" />
                        </div>
                      </div>
                      <div className="flex justify-center py-1.5">
                        <div className="w-[80px] h-[3px] bg-muted-foreground/30 rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Inputs */}
              <div className="flex-1 space-y-6">
                {/* Custom Message */}
                <div className="space-y-2">
                  <Label className="text-base font-semibold">
                    Custom Message
                  </Label>
                  <Textarea className="min-h-[220px] text-base rounded-xl border-2 focus:border-primary p-4 resize-none" />
                </div>

                {/* MMS Section */}
                <div className="space-y-2">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <ImageIcon className="h-4 w-4 text-muted-foreground" />
                    MMS (image to send)
                  </Label>
                  <div className="border-2 border-dashed border-border rounded-xl p-6 flex flex-col items-center justify-center min-h-[120px] bg-muted/20"></div>
                  <label htmlFor="mms-upload">
                    <Button
                      variant="outline"
                      className="w-full gap-2 h-11 rounded-xl text-base cursor-pointer"
                      asChild>
                      <span>
                        <Upload className="h-4 w-4" />
                        Add Images
                      </span>
                    </Button>
                  </label>
                  <input
                    id="mms-upload"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    className="hidden"
                  />
                  <p className="text-sm text-destructive font-medium">
                    Only .jpg/.png images will be accepted
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Actions */}
      <div className="pt-4 pb-8 flex justify-end gap-4">
        <Button
          variant="outline"
          size="lg"
          className="h-14 px-8 text-lg font-semibold rounded-xl">
          Close
        </Button>
        <Button
          size="lg"
          className="bg-primary hover:bg-primary/90 text-primary-foreground h-14 px-8 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3">
          <Save className="h-5 w-5" />
          Save Changes
        </Button>
      </div>
    </main>
  );
};

export default AppRegisterPageNew;
